/**
 * Facilitator panel (community-speech plan, Phase F — Leyu).
 *
 * Shown on the project dashboard ONLY to facilitators (and managers):
 * their attributed invite link — every contributor who joins through it
 * is stamped as their recruit — plus the cohort rollup (progress and
 * owner-declared amounts when the rate card is shared). Renders nothing
 * for everyone else; the server is the gate, this card just mirrors it.
 */
import { useCallback, useEffect, useState } from "react";
import { Button, SettingsCard, useToast } from "@humansignal/ui";
import { modal } from "../../components/Modal/Modal";
import { Pagination, usePage, usePageSize } from "../../components/Pagination/Pagination";
import { useTranslation } from "../../i18n/LocaleProvider";
import { qrSvgPath } from "../../utils/qr";

// Modal body for the invite QR. Deliberately black-on-white regardless
// of app theme — scanners want that contrast, and field posters get
// printed from this exact rendering.
const InviteQrBody = ({ url }) => {
  const { size, d } = qrSvgPath(url);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "8px 0 4px" }}>
      <svg
        width={280}
        height={280}
        viewBox={`0 0 ${size} ${size}`}
        shapeRendering="crispEdges"
        role="img"
        aria-label={url}
        style={{ background: "#fff", border: "1px solid var(--color-neutral-border, #e2e8f0)", borderRadius: 8 }}
      >
        <rect width={size} height={size} fill="#fff" />
        <path d={d} fill="#000" />
      </svg>
      <code style={{ fontSize: "var(--font-size-caption)", wordBreak: "break-all", textAlign: "center" }}>{url}</code>
    </div>
  );
};

const getCsrfToken = () => {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
};

// One cohort's recruit table. Own component so each cohort keeps its
// own page state; name/urlParamName are keyed by facilitator so two
// cohorts on the manager view never fight over the same URL param.
const CohortRecruits = ({ cohort, currency, t }) => {
  const [page, setPage] = usePage(`fcp${cohort.facilitator_id}`, 1);
  const [pageSize, setPageSize] = usePageSize(`fcp${cohort.facilitator_id}_size`, 10);
  const recruits = cohort.recruits || [];

  return (
    <>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--font-size-body-small)" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--color-neutral-content-subtler, #64748b)" }}>
              <th style={{ padding: "4px 6px" }}>{t("pages.projectdashboard.facilitator.recruit")}</th>
              <th style={{ padding: "4px 6px" }}>{t("pages.projectdashboard.facilitator.joined")}</th>
              <th style={{ padding: "4px 6px" }}>{t("pages.projectdashboard.facilitator.validated_units")}</th>
              <th style={{ padding: "4px 6px" }}>{t("pages.projectdashboard.facilitator.amount")}</th>
            </tr>
          </thead>
          <tbody>
            {recruits.slice((page - 1) * pageSize, page * pageSize).map((r) => {
              const units = Object.values(r.units || {}).reduce((s, v) => s + v, 0);
              let when = "";
              try {
                when = new Date(r.joined_at).toLocaleDateString();
              } catch {
                when = "";
              }
              return (
                <tr key={r.user_id} style={{ borderTop: "1px solid var(--color-neutral-border-subtle, #e2e8f0)" }}>
                  <td style={{ padding: "6px" }}>{r.name}</td>
                  <td style={{ padding: "6px" }}>{when}</td>
                  <td style={{ padding: "6px" }}>{units}</td>
                  <td style={{ padding: "6px", fontWeight: 600 }}>
                    {r.amount != null ? `${r.amount} ${currency}`.trim() : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {recruits.length > 0 && (
        <Pagination
          name={`facilitator-cohort-${cohort.facilitator_id}`}
          page={page}
          pageSize={pageSize}
          totalItems={recruits.length}
          urlParamName={`fcp${cohort.facilitator_id}`}
          pageSizeOptions={[10, 25, 50]}
          onPageLoad={(p, ps) => {
            setPage(p);
            setPageSize(ps);
          }}
          style={{ paddingTop: 12 }}
        />
      )}
    </>
  );
};

export const FacilitatorPanel = ({ projectId, canManage }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [joinUrl, setJoinUrl] = useState("");

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const url = `/api/projects/${projectId}/cohort/${canManage ? "?all=1" : ""}`;
      const r = await fetch(url, { credentials: "same-origin", cache: "no-store" });
      if (!r.ok) return; // 403 = not a facilitator — card stays hidden
      setData(await r.json());
    } catch {
      /* hidden */
    }
  }, [projectId, canManage]);
  useEffect(() => {
    load();
  }, [load]);

  // Create-or-get MY invite link; returns "" on failure so callers can
  // bail quietly (the endpoint 403s for non-facilitators).
  const fetchLink = useCallback(async () => {
    try {
      const r = await fetch(`/api/projects/${projectId}/facilitator-invite/`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRFToken": getCsrfToken() },
      });
      if (!r.ok) return "";
      const b = await r.json();
      const url = `${window.location.origin}${b.join_path}`;
      setJoinUrl(url);
      return url;
    } catch {
      return ""; /* transient */
    }
  }, [projectId]);

  const getLink = async () => {
    const url = await fetchLink();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.show({ message: t("pages.projectdashboard.facilitator.copied") });
    } catch {
      /* clipboard unavailable — the URL is displayed for manual copy */
    }
  };

  // In-person drives: participants scan instead of typing the URL.
  const showQr = async () => {
    const url = joinUrl || (await fetchLink());
    if (!url) return;
    modal({
      title: t("pages.projectdashboard.facilitator.qr_title"),
      style: { width: 380 },
      body: () => <InviteQrBody url={url} />,
      footer: (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="button"
            look="outlined"
            size="small"
            onClick={() => {
              navigator.clipboard?.writeText(url);
              toast.show({ message: t("pages.projectdashboard.facilitator.copied") });
            }}
          >
            {t("pages.projectdashboard.facilitator.copy_link")}
          </Button>
        </div>
      ),
    });
  };

  // Hidden until the server confirms the caller may see cohorts. A
  // manager with zero facilitators appointed still sees the card (so
  // the feature is discoverable), a plain contributor never does.
  if (!data) return null;
  if (!data.is_facilitator && !canManage) return null;

  const cohorts = data.cohorts || [];

  return (
    <SettingsCard
      title={t("pages.projectdashboard.facilitator.title")}
      description={t("pages.projectdashboard.facilitator.body")}
      headerActions={
        <div style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
          <Button type="button" look="outlined" size="small" onClick={getLink} style={{ minWidth: 104 }}>
            {t("pages.projectdashboard.facilitator.get_link")}
          </Button>
          <Button type="button" look="outlined" size="small" onClick={showQr} style={{ minWidth: 104 }}>
            {t("pages.projectdashboard.facilitator.qr")}
          </Button>
        </div>
      }
    >
      {joinUrl && (
        <div style={{ marginBottom: cohorts.length ? 16 : 0 }}>
          <code style={{ fontSize: "var(--font-size-caption)", wordBreak: "break-all" }}>{joinUrl}</code>
        </div>
      )}

      {cohorts.map((c) => (
        <div key={c.facilitator_id} style={{ marginBottom: 12 }}>
          {(canManage || cohorts.length > 1) && (
            <div style={{ fontSize: "var(--font-size-body-small)", fontWeight: 600, margin: "0 0 6px" }}>
              {c.facilitator_name}, {c.recruit_count} {t("pages.projectdashboard.facilitator.recruits_lc")}
              {c.total_amount != null && `, ${c.total_amount} ${data.currency}`.trimEnd()}
            </div>
          )}
          <CohortRecruits cohort={c} currency={data.currency} t={t} />
        </div>
      ))}
      {cohorts.length === 0 && (
        <p
          style={{
            margin: 0,
            fontSize: "var(--font-size-body-small)",
            color: "var(--color-neutral-content-subtler, #475569)",
          }}
        >
          {t("pages.projectdashboard.facilitator.empty")}
        </p>
      )}
    </SettingsCard>
  );
};
